if (!customElements.get('ecomhard-image-popup')) {
  class EcomhardImagePopup extends HTMLElement {
    constructor() {
      super();

      this.weekInMilliseconds = 7 * 24 * 60 * 60 * 1000;

      this.handleEscape = this.handleEscape.bind(this);
      this.closePopup = this.closePopup.bind(this);
      this.handleOutsideClick = this.handleOutsideClick.bind(this);
    }

    connectedCallback() {
      if (this.dataset.initialized === 'true') return;

      this.dataset.initialized = 'true';

      this.delay = Math.max(0, Number(this.dataset.delay) || 0);
      this.displayLimit = Math.max(1, Number(this.dataset.displayLimit) || 1);

      this.storageKey = this.dataset.storageKey || 'ecomhard-image-popup';
      this.cookieKey = `eh-popup-${this.storageKey.replace(/[^a-z0-9_-]/gi, '-')}`;
      this.sessionKey = `${this.storageKey}-shown-this-session`;

      this.dialog = this.querySelector('.eh-image-popup__dialog');
      this.closeButtons = this.querySelectorAll('[data-eh-popup-close]');

      this.closeButtons.forEach((button) => {
        button.addEventListener('click', this.closePopup);
      });

      this.addEventListener('click', this.handleOutsideClick);

      const startPopupTimer = () => {
        window.setTimeout(() => {
          this.openPopup();
        }, this.delay);
      };

      if (document.readyState === 'complete') {
        startPopupTimer();
      } else {
        window.addEventListener('load', startPopupTimer, { once: true });
      }
    }

    disconnectedCallback() {
      document.removeEventListener('keydown', this.handleEscape);

      this.closeButtons?.forEach((button) => {
        button.removeEventListener('click', this.closePopup);
      });

      this.removeEventListener('click', this.handleOutsideClick);
    }

    getCookieValue(name) {
      const prefix = `${name}=`;
      const cookies = document.cookie ? document.cookie.split('; ') : [];

      for (const cookie of cookies) {
        if (cookie.indexOf(prefix) === 0) {
          return decodeURIComponent(cookie.substring(prefix.length));
        }
      }

      return null;
    }

    hasShownInCurrentSession() {
      try {
        return window.sessionStorage.getItem(this.sessionKey) === 'true';
      } catch (error) {
        return false;
      }
    }

    markAsShownInCurrentSession() {
      try {
        window.sessionStorage.setItem(this.sessionKey, 'true');
      } catch (error) {
        // Caso sessionStorage esteja bloqueado, o limite semanal continua funcionando.
      }
    }

    getStoredPopupState() {
      let savedState = null;

      try {
        const localState = window.localStorage.getItem(this.storageKey);

        if (localState) {
          savedState = JSON.parse(localState);
        }
      } catch (error) {
        savedState = null;
      }

      if (!savedState) {
        try {
          const cookieState = this.getCookieValue(this.cookieKey);

          if (cookieState) {
            savedState = JSON.parse(cookieState);
          }
        } catch (error) {
          savedState = null;
        }
      }

      return savedState;
    }

    getPopupState() {
      const now = Date.now();
      const savedState = this.getStoredPopupState();

      if (
        !savedState ||
        !savedState.startedAt ||
        now < Number(savedState.startedAt) ||
        now - Number(savedState.startedAt) >= this.weekInMilliseconds
      ) {
        return {
          startedAt: now,
          count: 0
        };
      }

      return {
        startedAt: Number(savedState.startedAt),
        count: Math.max(0, Number(savedState.count) || 0)
      };
    }

    savePopupState(state) {
      const serializedState = JSON.stringify(state);

      try {
        window.localStorage.setItem(this.storageKey, serializedState);
      } catch (error) {
        // O cookie abaixo mantém a frequência funcionando caso localStorage esteja bloqueado.
      }

      try {
        document.cookie = `${this.cookieKey}=${encodeURIComponent(serializedState)}; path=/; max-age=${7 * 24 * 60 * 60}; samesite=lax`;
      } catch (error) {
        // Sem ação adicional necessária.
      }
    }

    openPopup() {
      if (!this.hidden) return;

      /*
       * Evita que o pop-up abra novamente ao navegar entre páginas
       * durante o mesmo acesso do usuário.
       */
      if (this.hasShownInCurrentSession()) {
        return;
      }

      const state = this.getPopupState();

      /*
       * Verifica se o visitante já atingiu o máximo de vezes
       * definido para a janela de 7 dias.
       */
      if (state.count >= this.displayLimit) {
        return;
      }

      state.count += 1;
      this.savePopupState(state);

      /*
       * Marca como exibido nesta sessão logo ao abrir.
       * Assim, mesmo que o usuário navegue para outra página,
       * o pop-up não volta a aparecer nesse mesmo acesso.
       */
      this.markAsShownInCurrentSession();

      this.hidden = false;
      this.setAttribute('aria-hidden', 'false');

      document.body.classList.add('eh-image-popup-is-open');
      document.addEventListener('keydown', this.handleEscape);

      window.setTimeout(() => {
        this.dialog?.focus();
      }, 50);
    }

    closePopup() {
      if (this.hidden) return;

      this.hidden = true;
      this.setAttribute('aria-hidden', 'true');

      document.body.classList.remove('eh-image-popup-is-open');
      document.removeEventListener('keydown', this.handleEscape);
    }

    handleEscape(event) {
      if (event.key === 'Escape') {
        this.closePopup();
      }
    }

    handleOutsideClick(event) {
      const clickedInsideDialog = event.target.closest('.eh-image-popup__dialog');

      if (!clickedInsideDialog) {
        this.closePopup();
      }
    }
  }

  customElements.define('ecomhard-image-popup', EcomhardImagePopup);
}